import { useState } from "react";
import { toast } from "sonner";
import { Plus, CheckSquare, Phone, Trash2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/Brand";
import { api, errMsg, useData } from "@/lib/api";
import { fullName, TEMP_STYLE } from "@/lib/crm";
import { useAuth } from "@/context/AuthContext";
import { useStickyState } from "@/lib/useStickyState";
import { useNavigate } from "react-router-dom";

const TYPES = ["Call", "Follow-up Email", "Session Prep", "Review Note"];
const PRIORITIES = ["High", "Medium", "Low"];
const PRIORITY_STYLE = { High: "bg-rose-50 text-rose-700 border-rose-200", Medium: "bg-amber-50 text-amber-700 border-amber-200", Low: "bg-stone-100 text-stone-600 border-stone-200" };
const TABS = [["all", "All Tasks"], ["today", "Due Today"], ["calls", "Calls to Make"], ["done", "Completed"]];
const NONE = "__none__";

function TaskDialog({ open, onOpenChange, contacts, associates, user, onSaved }) {
  const blank = { type: "Call", title: "", contact_id: null, due_date: "", due_time: "", priority: "Medium", assignee: user.name, note: "" };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reminders", { ...f, contact_id: f.contact_id || null });
      toast.success("Task created");
      setF(blank);
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="task-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">New Task</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger data-testid="task-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Priority</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}><SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Title *</Label><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Follow up about session enrollment" data-testid="task-title-input" /></div>
          <div className="space-y-1.5"><Label>Related lead</Label>
            <Select value={f.contact_id || NONE} onValueChange={(v) => setF({ ...f, contact_id: v === NONE ? null : v })}><SelectTrigger data-testid="task-contact-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent><SelectItem value={NONE}>No lead</SelectItem>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{fullName(c)}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Due date</Label><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="task-date-input" /></div>
            <div className="space-y-1.5"><Label>Due time</Label><Input type="time" value={f.due_time} onChange={(e) => setF({ ...f, due_time: e.target.value })} data-testid="task-time-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Assign to (VA)</Label>
            {associates.length ? (
              <Select value={f.assignee} onValueChange={(v) => setF({ ...f, assignee: v })}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Select VA" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={user.name}>{user.name} (me)</SelectItem>
                  {associates.map((a) => <SelectItem key={a.id} value={a.name} data-testid={`task-assignee-option-${a.id}`}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input value={f.assignee} onChange={(e) => setF({ ...f, assignee: e.target.value })} data-testid="task-assignee-input" />
            )}
          </div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="task-note-input" /></div>
          <DialogFooter><Button type="submit" disabled={busy} className="bg-[#3B185F]" data-testid="task-save-btn">{busy ? "Saving…" : "Create Task"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Tasks() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [tasks, reload] = useData("/reminders", []);
  const [contacts] = useData("/contacts", []);
  const [associates] = useData(isAdmin ? "/admin/students" : null, []);
  const [tab, setTab] = useStickyState("tasks.tab", "all");
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const tempOf = (cid) => contacts.find((c) => c.id === cid)?.temperature;

  const complete = async (t) => { try { await api.patch(`/reminders/${t.id}`, { done: !t.done }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (t) => { try { await api.delete(`/reminders/${t.id}`); toast.success("Task removed"); reload(); } catch (e) { toast.error(errMsg(e)); } };

  const shown = tasks.filter((t) => {
    if (tab === "done") return t.done;
    if (tab === "today") return !t.done && t.due_date === today;
    if (tab === "calls") return !t.done && t.type === "Call";
    return !t.done;
  });

  return (
    <div>
      <PageHeader title="Tasks & Follow-ups" subtitle="Create reminders and schedule follow-up calls against your leads.">
        <Button onClick={() => setOpen(true)} className="bg-[#3B185F]" data-testid="add-task-btn"><Plus className="h-4 w-4 mr-1.5" />New Task</Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map(([v, l]) => (
          <button key={v} type="button" onClick={() => setTab(v)} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === v ? "bg-[#3B185F] text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`} data-testid={`tasks-tab-${v}`}>
            {l} <span className="opacity-60">{tasks.filter((t) => v === "done" ? t.done : v === "today" ? (!t.done && t.due_date === today) : v === "calls" ? (!t.done && t.type === "Call") : !t.done).length}</span>
          </button>
        ))}
      </div>

      <div className="space-y-2" data-testid="tasks-list">
        {!shown.length && <div className="rounded-2xl border border-dashed p-12 text-center text-stone-500" data-testid="tasks-empty">No tasks here. Create one to get started.</div>}
        {shown.map((t) => (
          <div key={t.id} className="flex items-start gap-3 rounded-2xl border bg-white p-4" data-testid={`task-row-${t.id}`}>
            <button type="button" onClick={() => complete(t)} className={`mt-0.5 grid h-5 w-5 place-items-center rounded border ${t.done ? "bg-[#059669] border-[#059669] text-white" : "border-stone-300 hover:border-[#3B185F]"}`} data-testid={`task-complete-${t.id}`}>
              {t.done && <Check className="h-3.5 w-3.5" />}
            </button>
            <div className="min-w-0 flex-1">
              <div className={`font-semibold text-[#19082B] ${t.done ? "line-through text-stone-400" : ""}`}>{t.title}</div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-stone-500">
                <span className="rounded-full bg-[#F6F2FB] px-2 py-0.5 text-[#3B185F] font-medium">{t.type}</span>
                <span className={`rounded-full border px-2 py-0.5 font-medium ${PRIORITY_STYLE[t.priority]}`}>{t.priority}</span>
                {t.contact_name && <span>· {t.contact_name}</span>}
                {t.contact_id && tempOf(t.contact_id) && <span className={`rounded-full border px-1.5 text-[9px] font-bold ${TEMP_STYLE[tempOf(t.contact_id)]}`} data-testid={`task-temp-${t.id}`}>{tempOf(t.contact_id)}</span>}
                {t.assignee && <span>· assigned to {t.assignee}</span>}
                {t.due_date && <span>· due {t.due_date}{t.due_time ? ` ${t.due_time}` : ""}</span>}
              </div>
              {t.note && <div className="mt-1 text-sm text-stone-600">{t.note}</div>}
            </div>
            <div className="flex items-center gap-1">
              {t.type === "Call" && t.contact_id && <button type="button" onClick={() => navigate("/dialer", { state: { contactId: t.contact_id } })} className="rounded-md border p-1.5 text-[#3B185F] hover:bg-[#F6F2FB]" title="Dial" data-testid={`task-dial-${t.id}`}><Phone className="h-4 w-4" /></button>}
              <button type="button" onClick={() => remove(t)} className="rounded-md p-1.5 text-stone-300 hover:text-red-600" title="Delete" data-testid={`task-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <TaskDialog open={open} onOpenChange={setOpen} contacts={contacts} associates={associates} user={user} onSaved={reload} />
    </div>
  );
}
